import { getRouteApi } from "@tanstack/react-router";
import { getCurrentMonth, getMonthYearOptions, parseMonthYearValue, createMonthYearValue } from "../utils/monthUtils";
import { SelectInput } from "@/components/SelectInput";
import { Heading4 } from "@/components/Typography";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";

const routeApi = getRouteApi("/(authed)/_layout/bookings/_tabbed/summary");

interface SummaryHeaderProps {
  title: string;
}

export function SummaryHeader({ title }: SummaryHeaderProps) {
  const navigate = routeApi.useNavigate();
  const search = routeApi.useSearch();
  const { toast } = useToast();

  const isDestinations = search.view === "destinations";

  const currentMonthResult = getCurrentMonth();
  const currentMonth = currentMonthResult.success ? currentMonthResult.data : "january";
  const currentYear = new Date().getFullYear();

  const selectedMonth = search.month || currentMonth;
  const selectedYear = search.year || currentYear;
  const selectedMonthYear = createMonthYearValue(selectedMonth, selectedYear);

  const handleSwitchChange = (checked: boolean) => {
    void navigate({
      search: (prev) => ({
        ...prev,
        view: checked ? "sources" : "destinations",
      }),
    });
  };

  const handleMonthYearChange = (monthYearValue: string) => {
    const parsed = parseMonthYearValue(monthYearValue);
    if (!parsed) {
      toast({
        title: "Something went wrong",
        description: "Unable to process the selected date. Please try refreshing the page.",
        variant: "destructive",
      });
      return;
    }

    void navigate({
      search: (prev) => ({
        ...prev,
        month: parsed.month,
        year: parsed.year,
      }),
    });
  };

  const monthYearOptions = getMonthYearOptions();

  return (
    <div className="space-y-4 mb-4">
      <Heading4>{title}</Heading4>

      <div className="flex items-center justify-between">
        <div className="w-40">
          <SelectInput<string>
            value={selectedMonthYear}
            onChange={handleMonthYearChange}
            options={monthYearOptions}
            placeholder="Select"
            aria-label="Select month and year"
            showClear={false}
          />
        </div>

        <div className="flex items-center space-x-3">
          <Label htmlFor="view-switch" className="text-sm font-medium text-muted-foreground">
            Destinations
          </Label>
          <Switch id="view-switch" checked={!isDestinations} onCheckedChange={handleSwitchChange} />
          <Label htmlFor="view-switch" className="text-sm font-medium text-muted-foreground">
            Sources
          </Label>
        </div>
      </div>
    </div>
  );
}
